import { useEffect } from "react";

export interface KeyboardSafeFieldScrollInput {
  fieldTop: number;
  fieldBottom: number;
  visualViewportHeight: number;
  visualViewportOffsetTop: number;
  keyboardOffset: number;
  margin?: number;
}

export interface KeyboardSafeFieldScrollPlan {
  needsScroll: boolean;
  block: ScrollLogicalPosition;
  visibleTop: number;
  visibleBottom: number;
}

export function deriveVisualViewportKeyboardOffset(input: {
  layoutViewportHeight: number;
  visualViewportHeight: number;
  visualViewportOffsetTop: number;
}): number {
  const { layoutViewportHeight, visualViewportHeight, visualViewportOffsetTop } = input;
  if (!Number.isFinite(layoutViewportHeight) || !Number.isFinite(visualViewportHeight) || !Number.isFinite(visualViewportOffsetTop)) {
    return 0;
  }
  return Math.round(Math.max(0, layoutViewportHeight - visualViewportHeight - visualViewportOffsetTop));
}

export function deriveKeyboardSafeFieldScrollPlan(input: KeyboardSafeFieldScrollInput): KeyboardSafeFieldScrollPlan {
  const margin = Number.isFinite(input.margin) ? Math.max(0, input.margin ?? 0) : 24;
  const valid = [
    input.fieldTop,
    input.fieldBottom,
    input.visualViewportHeight,
    input.visualViewportOffsetTop,
    input.keyboardOffset,
  ].every(Number.isFinite);

  if (!valid || input.visualViewportHeight <= 0) {
    return { needsScroll: false, block: "nearest", visibleTop: 0, visibleBottom: 0 };
  }

  const keyboardOpen = input.keyboardOffset > 0;
  const visibleTop = input.visualViewportOffsetTop + margin;
  const visibleBottom = input.visualViewportOffsetTop + input.visualViewportHeight - (keyboardOpen ? Math.max(72, margin) : margin);
  const needsScroll = input.fieldBottom > visibleBottom || input.fieldTop < visibleTop;

  return {
    needsScroll,
    block: keyboardOpen ? "center" : "nearest",
    visibleTop: Math.round(visibleTop),
    visibleBottom: Math.round(visibleBottom),
  };
}

function isKeyboardSafeField(target: EventTarget | null): target is HTMLElement {
  if (!(target instanceof HTMLElement)) return false;
  return target.matches(
    [
      ".simo-keyboard-safe-field",
      ".simo-command-input",
      ".simo-dashboard-main input:not([type='checkbox']):not([type='radio'])",
      ".simo-dashboard-main select",
      ".simo-dashboard-main textarea",
      ".simo-owner-side-panel input:not([type='checkbox']):not([type='radio'])",
      ".simo-owner-side-panel select",
      ".simo-owner-side-panel textarea",
      ".simo-cosmic-session-panel input:not([type='checkbox']):not([type='radio'])",
      ".simo-cosmic-session-panel textarea",
      ".simo-keyboard-safe-editor input:not([type='checkbox']):not([type='radio'])",
      ".simo-keyboard-safe-editor select",
      ".simo-keyboard-safe-editor textarea",
    ].join(", "),
  );
}

export function useKeyboardSafeViewport(): void {
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;

    const root = document.documentElement;
    const viewport = window.visualViewport;
    let frame = 0;
    let focusTimer = 0;
    let settleTimer = 0;
    let keyboardOffset = 0;

    const scrollElementIfCovered = (target: HTMLElement, behavior: ScrollBehavior) => {
      const rect = target.getBoundingClientRect();
      const plan = deriveKeyboardSafeFieldScrollPlan({
        fieldTop: rect.top,
        fieldBottom: rect.bottom,
        visualViewportHeight: viewport?.height ?? window.innerHeight,
        visualViewportOffsetTop: viewport?.offsetTop ?? 0,
        keyboardOffset,
      });
      if (!plan.needsScroll) return;

      target.closest<HTMLElement>(".simo-keyboard-safe-editor")?.scrollIntoView({ block: plan.block, behavior });
      target.scrollIntoView({ block: plan.block, behavior });
    };

    const scrollActiveFieldIfCovered = (behavior: ScrollBehavior = "smooth") => {
      const active = document.activeElement;
      if (!isKeyboardSafeField(active)) return;
      scrollElementIfCovered(active, behavior);
    };

    const update = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        keyboardOffset = viewport
          ? deriveVisualViewportKeyboardOffset({
              layoutViewportHeight: window.innerHeight,
              visualViewportHeight: viewport.height,
              visualViewportOffsetTop: viewport.offsetTop,
            })
          : 0;
        root.style.setProperty("--simo-keyboard-offset", `${keyboardOffset}px`);
        root.style.setProperty("--simo-visual-viewport-height", `${Math.round(viewport?.height ?? window.innerHeight)}px`);
        root.dataset.simoKeyboard = keyboardOffset > 0 ? "open" : "closed";

        if (settleTimer) window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(() => scrollActiveFieldIfCovered("smooth"), keyboardOffset > 0 ? 120 : 0);
      });
    };

    const scrollFocusedField = (event: FocusEvent) => {
      if (!isKeyboardSafeField(event.target)) return;
      if (focusTimer) window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => {
        if (!isKeyboardSafeField(event.target)) return;
        scrollElementIfCovered(event.target, "smooth");
      }, 80);
    };

    update();
    viewport?.addEventListener("resize", update);
    viewport?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    document.addEventListener("focusin", scrollFocusedField);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      if (focusTimer) window.clearTimeout(focusTimer);
      if (settleTimer) window.clearTimeout(settleTimer);
      viewport?.removeEventListener("resize", update);
      viewport?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      document.removeEventListener("focusin", scrollFocusedField);
      root.style.removeProperty("--simo-keyboard-offset");
      root.style.removeProperty("--simo-visual-viewport-height");
      delete root.dataset.simoKeyboard;
    };
  }, []);
}
